'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

export interface PipelineJob {
  id: string;
  customer: string;
  address: string;
  job_status: string;
  progress: number;
  crew: string;
}

interface Row4PipelineProductionProps {
  jobs: PipelineJob[];
  bottleneckCounts: { permits: number; materials: number; changeOrders: number } | null;
  topBlockedPermit: { jurisdiction: string; daysWaiting: number } | null;
  bottlenecksLoading: boolean;
  bottlenecksError: string | null;
}

const statusColor = (status: string): { bg: string; fg: string } => {
  if (status === 'in-progress' || status === 'active') return { bg: 'rgba(7,176,233,0.10)', fg: '#07B0E9' };
  if (status === 'completed')                            return { bg: 'rgba(16,185,129,0.10)', fg: '#10B981' };
  if (status === 'delayed')                              return { bg: 'rgba(239,68,68,0.10)', fg: '#EF4444' };
  if (status === 'scheduled')                            return { bg: 'rgba(245,158,11,0.10)', fg: '#F59E0B' };
  return { bg: 'rgba(107,114,128,0.10)', fg: '#6B7280' };
};

// ── Mock production-health stat tiles ──────────────────────────────────────
const HEALTH_TILES: Array<{
  label: string;
  value: string;
  delta: string;
  tone: 'up' | 'down' | 'neutral';
  icon: string;
  color: string;
}> = [];

export default function Row4PipelineProduction({
  jobs,
  bottleneckCounts,
  topBlockedPermit,
  bottlenecksLoading,
  bottlenecksError,
}: Row4PipelineProductionProps) {
  const displayJobs = jobs.slice(0, 6);

  const liveBottlenecks: Array<{ label: string; icon: string; color: string }> =
    bottleneckCounts
      ? [
          { label: `${bottleneckCounts.permits} jobs blocked on permit`,    icon: 'DocumentCheckIcon', color: '#F59E0B' },
          { label: `${bottleneckCounts.materials} material alerts`,         icon: 'CubeIcon',          color: '#EF4444' },
          { label: `${bottleneckCounts.changeOrders} change orders pending signature`, icon: 'DocumentTextIcon', color: '#07B0E9' },
        ]
      : [];

  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* ── Job Pipeline ────────────────────────────────────────────────── */}
      <div
        className="relative rounded-xl p-5"
        style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Icon name="BriefcaseIcon" size={15} style={{ color: '#07B0E9' }} />
          <h3
            className="text-base font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            Job Pipeline
          </h3>
          <span className="ml-auto text-xs text-gray-400">{jobs.length} active</span>
        </div>
        {displayJobs.length === 0 ? (
          <div className="py-8 text-center">
            <Icon name="CheckCircleIcon" size={28} style={{ color: '#10B981' }} className="mx-auto mb-2" />
            <p className="text-xs text-gray-500">No active jobs in pipeline</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayJobs.map((job) => {
              const sc = statusColor(job.job_status);
              return (
                <div key={job.id} className="rounded-lg px-3 py-2" style={{ border: '1px solid #F3F4F6' }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-mono text-gray-700 truncate">{job.id}</p>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
                      style={{ background: sc.bg, color: sc.fg }}
                    >
                      {job.job_status}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">
                    {job.customer} · {job.address}
                  </p>
                  <div className="mt-1.5 h-1 rounded-full" style={{ background: '#F3F4F6' }}>
                    <div
                      className="h-1 rounded-full"
                      style={{ width: `${Math.max(0, Math.min(100, job.progress))}%`, background: sc.fg }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Bottlenecks callout — live counts from /api/operations/bottlenecks */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Bottlenecks
          </p>
          {bottlenecksLoading ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : bottlenecksError ? (
            <p className="text-xs" style={{ color: '#B91C1C' }}>
              Unable to load bottleneck counts
            </p>
          ) : (
            <div className={topBlockedPermit ? 'grid grid-cols-2 gap-3' : ''}>
              <ul className="space-y-1.5">
                {liveBottlenecks.map((b) => (
                  <li key={b.label} className="flex items-center gap-2 text-xs text-gray-700">
                    <Icon name={b.icon} size={13} style={{ color: b.color }} />
                    <span>{b.label}</span>
                  </li>
                ))}
              </ul>
              {topBlockedPermit && (
                <div
                  className="rounded-lg px-3 py-2"
                  style={{ border: '1px solid #FEE2E2', background: 'rgba(239,68,68,0.04)' }}
                >
                  <p
                    className="text-[10px] uppercase tracking-wider font-semibold"
                    style={{ color: '#92400E' }}
                  >
                    Top blocked
                  </p>
                  <p className="text-xs font-semibold text-gray-800 truncate mt-0.5">
                    {topBlockedPermit.jurisdiction}
                  </p>
                  <p className="text-[11px] font-bold" style={{ color: '#B91C1C' }}>
                    {topBlockedPermit.daysWaiting}d waiting
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Production Health ──────────────────────────────────────────── */}
      <div
        className="relative rounded-xl p-5"
        style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
      >
        <DemoDataPill />
        <div className="flex items-center gap-2 mb-4">
          <Icon name="ChartBarIcon" size={15} style={{ color: '#033494' }} />
          <h3
            className="text-base font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            Production Health
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {HEALTH_TILES.map((tile) => {
            const deltaColor =
              tile.tone === 'up' ? '#10B981'
              : tile.tone === 'down' ? '#EF4444'
              : '#6B7280';
            const arrow = tile.tone === 'up' ? '▲' : tile.tone === 'down' ? '▼' : '·';
            return (
              <div
                key={tile.label}
                className="rounded-lg p-3 min-w-0"
                style={{ background: 'rgba(248,250,252,0.7)', border: '1px solid #F3F4F6' }}
              >
                <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                  <Icon name={tile.icon} size={12} style={{ color: tile.color }} />
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 truncate">{tile.label}</p>
                </div>
                <p
                  className="text-2xl font-bold leading-tight"
                  style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
                >
                  {tile.value}
                </p>
                <p className="text-[11px] font-medium mt-0.5 truncate" style={{ color: deltaColor }}>
                  <span className="font-bold mr-1">{arrow}</span>{tile.delta}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
